// Header.tsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import axios from "../utils/axiosConfig";
import { useWebSocket } from "../providers/WebSocketProvider";
import { UserMenu } from "./ui/userMenu";
import { LoginDrawer } from "./LoginDrawer";

// Navigation for authenticated users
const authNavigation = [
  { name: "Projects", href: "/" },
  { name: "Files", href: "/files" },
  { name: "Buy points", href: "/price" },
  { name: "Voices", href: "/voices" },
  { name: "How It Works", href: "/faq" },
];

// Navigation for unauthenticated users
const publicNavigation = [
  { name: "Home", href: "/landing" },
  { name: "About", href: "/about" },
  { name: "Pricing", href: "/price" },
  { name: "How It Works", href: "/faq" },
];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [points, setPoints] = useState<number>(0);
  const { eventBus } = useWebSocket();
  const userName = localStorage.getItem("userName") || "User";
  const location = useLocation();
  
  // Check for token to determine authentication status
  const isAuthenticated = localStorage.getItem("token") !== null;
  
  // Use the appropriate navigation depending on the authentication status
  const navigation = isAuthenticated ? authNavigation : publicNavigation;

  // Initial points fetch (REST) + WS updates
  useEffect(() => {
    const handlePoints = (data: number) => setPoints(data);
    eventBus.on("points", handlePoints);
    if (isAuthenticated) {
      axios.get<{ points: number }>("/points").then((r) => setPoints(r.data.points)).catch(() => {});
    }
    return () => {
      eventBus.off("points", handlePoints);
    };
  }, [eventBus, isAuthenticated]);

  // Open login drawer
  const openLogin = () => {
    setIsLoginOpen(true);
    // Close mobile menu if it's open
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  // Close login drawer
  const closeLogin = () => {
    setIsLoginOpen(false);
  };

  return (
    <>
      <header className="app-header relative z-30 w-full py-4 px-6 md:px-12 border-b border-border/40 backdrop-blur-md bg-background/80">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to={isAuthenticated ? "/" : "/landing"} className="flex items-center gap-2">
            {/* <div className="h-8 w-8 rounded-full bg-gradient-divine" /> */}
            <img src="/DE_logo_only_250x250.png" alt="Divine Echo" className="h-8 w-8" />
            <span className="text-xl font-semibold font-['Playfair_Display']">
              Divine Echo
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-foreground/80 hover:text-foreground transition-colors ${
                    isActive ? "font-semibold" : ""
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User menu or Auth buttons (desktop) */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <UserMenu userName={userName} points={points || undefined} />
            ) : (
              <>
                <button 
                  onClick={openLogin}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Sign In
                </button>
                <Link to="/register">
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                    Get Started
                  </button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? (
                <X className="h-5 w-5 text-foreground/80" />
              ) : (
                <Menu className="h-5 w-5 text-foreground/80" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-background border-b border-border/40 py-4 px-6 shadow-lg">
            <nav className="flex flex-col gap-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-foreground/80 hover:text-foreground transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Auth buttons or Logout for mobile */}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/settings"
                    className="text-foreground/80 hover:text-foreground transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Settings
                  </Link>
                  <Link
                    to="/logout"
                    className="text-foreground/80 hover:text-foreground transition-colors py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Logout
                  </Link>
                </>
              ) : (
                <div className="flex gap-4 mt-4">
                  <button
                    onClick={openLogin}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Sign In
                  </button>
                  <Link to="/login" className="flex-1" onClick={() => setIsMobileMenuOpen(false)}>
                    <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                      Get Started
                    </button>
                  </Link>
                </div>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Login drawer */}
      <LoginDrawer isOpen={isLoginOpen} onClose={closeLogin} />
    </>
  );
};

export default Header;
